import { getOrCreateCorrelationId, getOrCreateAnonymousId } from "./visitTracking";
import { recordPricingClickEvent } from "@/api/PricingApi";
import { ELIXIR_URL } from "@/constastants/Constant";
import { appendUtmParams, resolveUtmParams } from "./utmParams";

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign"];

const buildUtmMetadata = (query = {}) => {
  const metadata = {};
  UTM_KEYS.forEach((key) => {
    if (query[key]) metadata[key] = query[key];
  });
  return metadata;
};

/**
 * Handles a "Request Quote" / "Request Sample/Quote" / "Sign Up & View Price" CTA
 * click on the website: always records the click (fire-and-forget), then
 * either redirects to the dashboard login (price available) or opens the
 * contact-us modal (price unavailable).
 *
 * See the Customer Pricing Flow API Contract (has_price) and the Website
 * Visit Handoff integration contract (event fields, visit_ref redirect).
 *
 * `utmMedium` is the default utm_medium for the redirect (page UTMs still win);
 * pass it to tag a specific placement, e.g. "product_page_banner".
 */
export const handlePricingCtaClick = ({
  hasPrice,
  ctaType,
  cpdGradeMasterId,
  v2ProductId,
  v2GradeId,
  chemicalName,
  brandName,
  router,
  onOpenModal,
  utmMedium = "product_page",
}) => {
  const query = router?.query || {};
  const correlationId = getOrCreateCorrelationId();

  const payload = {
    correlation_id: correlationId,
    anonymous_id: getOrCreateAnonymousId(),
    cta_type: ctaType,
    target_id: cpdGradeMasterId || undefined,
    target_type: "cpd_grade",
    target_label: brandName ? `${chemicalName} - ${brandName}` : chemicalName,
    occurred_at: new Date().toISOString(),
    metadata: buildUtmMetadata(query),
    source: "website",
  };

  // Fire-and-forget — never blocks the redirect/modal on this call.
  recordPricingClickEvent(payload);

  if (hasPrice) {
    const dashboardUrl = ELIXIR_URL.replace(/\/+$/, "");
    const utm = resolveUtmParams(query, { source: "website", medium: utmMedium });
    const params = new URLSearchParams({ visit_ref: correlationId });
    if (v2ProductId) params.set("v2_product_id", v2ProductId);
    if (v2GradeId) params.set("v2_grade_id", v2GradeId);
    const redirectUrl = appendUtmParams(
      `${dashboardUrl}/login/otp?${params.toString()}`,
      utm,
    );
    window.location.href = redirectUrl;
    return;
  }

  onOpenModal?.();
};

// Elixir route the catalogue login lands on. Elixir prefixes the user's
// tenant id itself (→ /<tenant-id>/external/product/), so it is not included.
const ELIXIR_CATALOGUE_RETURN_TO = "/external/product/";

/**
 * Sends the visitor to Elixir's OTP login, returning to the external product
 * catalogue (same tab). Used by the product page's Elixir CTAs that aren't tied
 * to a priced grade. Page UTMs win over the defaults (see resolveUtmParams).
 *
 * @param {Record<string, any>} [query] - router.query
 * @param {string} [utmMedium]
 */
export const redirectToElixirCatalogueLogin = (query = {}, utmMedium = "product_page") => {
  const utm = resolveUtmParams(query, { source: "website", medium: utmMedium });
  const params = new URLSearchParams({ returnTo: ELIXIR_CATALOGUE_RETURN_TO });
  const loginUrl = `${ELIXIR_URL.replace(/\/+$/, "")}/login/otp/?${params.toString()}`;
  window.location.href = appendUtmParams(loginUrl, utm);
};
